'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { LayoutDashboard, ClipboardList, Package, CheckSquare, BarChart3, Calendar, ChevronLeft, ChevronRight, User, Bell, Wrench, Users, ListChecks, LogOut, PieChart } from 'lucide-react';
import { getSession, clearSession, TechnicianSession } from '@/lib/technicianSession';

const navGroups = [
  {
    label: 'Core Modules',
    items: [
      { href: '/', label: 'Dashboard', icon: LayoutDashboard, badge: null },
      { href: '/job-card-form', label: 'Job Card', icon: ClipboardList, badge: null },
      { href: '/inventory-management', label: 'Inventory', icon: Package, badge: null },
      { href: '/parts-management', label: 'Parts Management', icon: ListChecks, badge: null },
    ],
  },
  {
    label: 'Fleet',
    items: [
      { href: '/work-requests', label: 'Work Requests', icon: Calendar, badge: null },
      { href: '/cosmetic-checklist', label: 'Cosmetic List', icon: CheckSquare, badge: null },
      { href: '/weekly-report', label: 'Weekly Report', icon: BarChart3, badge: null },
      { href: '/reports', label: 'Reports', icon: PieChart, badge: null },
    ],
  },
  {
    label: 'Admin',
    items: [
      { href: '/technicians', label: 'Technicians', icon: Users, badge: null },
    ],
  },
];

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [session, setSession] = useState<TechnicianSession | null>(null);
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    setSession(getSession());
  }, [pathname]);

  // Auto-collapse on tablet (md breakpoint) to save space
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && window.innerWidth < 1280) {
        setCollapsed(true);
      } else if (window.innerWidth >= 1280) {
        setCollapsed(false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleClockOut = () => {
    clearSession();
    router.push('/login');
  };

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  return (
    <>
      {/* Mobile/tablet overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile/tablet toggle — touch-friendly size */}
      <button
        className="fixed top-4 left-4 z-50 lg:hidden p-2.5 rounded-lg card-surface min-w-[44px] min-h-[44px] flex items-center justify-center"
        onClick={() => setMobileOpen(!mobileOpen)}
        aria-label="Toggle navigation"
      >
        <Wrench size={20} className="text-primary" />
      </button>

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:relative inset-y-0 left-0 z-50 flex flex-col
          transition-all duration-300 ease-in-out
          ${collapsed ? 'w-16' : 'w-60'}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        style={{ backgroundColor: '#0a1628', borderRight: '1px solid var(--border)' }}
      >
        {/* Logo */}
        <div className={`flex items-center gap-3 px-4 py-5 border-b border-border ${collapsed ? 'justify-center' : ''}`}>
          <AppLogo size={32} />
          {!collapsed && (
            <div>
              <span className="font-bold text-sm text-foreground tracking-tight">Fss Job Card</span>
              <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Workshop Terminal</p>
            </div>
          )}
        </div>

        {/* Nav groups */}
        <nav className="flex-1 overflow-y-auto scrollbar-thin py-4 px-2">
          {navGroups.map((group) => (
            <div key={`group-${group.label}`} className="mb-6">
              {!collapsed && (
                <p className="px-2 mb-2 text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--muted-foreground)' }}>
                  {group.label}
                </p>
              )}
              <ul className="space-y-1">
                {group.items.map((item) => {
                  const active = isActive(item.href);
                  return (
                    <li key={`nav-${item.href}`}>
                      <Link
                        href={item.href}
                        onClick={() => setMobileOpen(false)}
                        title={collapsed ? item.label : undefined}
                        className={`
                          flex items-center gap-3 px-2.5 py-2.5 rounded-lg text-sm font-medium
                          transition-all duration-150 group relative min-h-[44px]
                          ${active
                            ? 'bg-primary/10 text-primary border border-primary/20' :'text-muted-foreground hover:bg-secondary hover:text-foreground'
                          }
                          ${collapsed ? 'justify-center' : ''}
                        `}
                      >
                        <item.icon size={18} className={`flex-shrink-0 ${active ? 'text-primary' : ''}`} />
                        {!collapsed && (
                          <>
                            <span className="flex-1">{item.label}</span>
                            {item.badge && (
                              <span className="badge badge-warning text-xs px-1.5 py-0.5">
                                {item.badge}
                              </span>
                            )}
                          </>
                        )}
                        {/* Collapsed tooltip */}
                        {collapsed && (
                          <span className="
                            absolute left-full ml-2 px-2 py-1 rounded text-xs font-medium
                            bg-card border border-border text-foreground whitespace-nowrap
                            opacity-0 group-hover:opacity-100 pointer-events-none
                            transition-opacity duration-150 z-50
                          ">
                            {item.label}
                            {item.badge && ` (${item.badge})`}
                          </span>
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        {/* Bottom actions */}
        <div className="border-t border-border p-2 space-y-1">
          {!collapsed && session && (
            <div className="px-2.5 py-2 mb-1">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <User size={14} className="text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-foreground truncate">{session.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{session.branch}</p>
                </div>
                <Bell size={14} className="text-muted-foreground flex-shrink-0" />
              </div>
            </div>
          )}
          {!collapsed && session && (
            <button
              onClick={handleClockOut}
              className="w-full flex items-center gap-2 px-2.5 py-2.5 rounded-lg text-xs text-danger hover:bg-danger/10 transition-all duration-150 min-h-[44px]"
            >
              <LogOut size={14} />
              <span>Clock Out</span>
            </button>
          )}
          {collapsed && session && (
            <button
              onClick={handleClockOut}
              title="Clock Out"
              className="w-full flex items-center justify-center px-2.5 py-2.5 rounded-lg text-danger hover:bg-danger/10 transition-all duration-150 min-h-[44px]"
            >
              <LogOut size={16} />
            </button>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-full flex items-center justify-center gap-2 px-2.5 py-2.5 rounded-lg text-sm text-muted-foreground hover:bg-secondary hover:text-foreground transition-all duration-150 min-h-[44px]"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight size={16} /> : (
              <>
                <ChevronLeft size={16} />
                <span className="text-xs">Collapse</span>
              </>
            )}
          </button>
        </div>
      </aside>
    </>
  );
}